import React, { useState, useEffect } from 'react';
import { Line, Bar, Pie } from 'react-chartjs-2';
import axios from 'axios';
import { GLOBALS } from '../../utils/Globals';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
} from 'chart.js';
import 'chartjs-adapter-date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

const ProductAnalytics = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const baseURLGetAllPDFDocs =
          process.env.REACT_APP_ENV === "PROD"
            ? GLOBALS.PROD.baseURLGetAllPDFDocs
            : GLOBALS.DEV.baseURLGetAllPDFDocs;

        const response = await axios.get(baseURLGetAllPDFDocs);
        setData(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div>Loading charts...</div>;
  }

  // Process data for products over time
  const timelineData = {
    labels: data.map(item => new Date(item.metadata.timestamp)),
    datasets: [{
      label: 'Number of Products',
      data: data.map(item => item.metadata.number_of_products),
      fill: false,
      borderColor: 'rgb(75, 192, 192)',
      tension: 0.1
    }]
  };

  // Process data for user productivity
  const userProductivity = data.reduce((acc, item) => {
    const email = item.metadata.email;
    acc[email] = (acc[email] || 0) + (item.metadata.number_of_products || 0);
    return acc;
  }, {});

  const userProductivityData = {
    labels: Object.keys(userProductivity),
    datasets: [{
      label: 'Products Created',
      data: Object.values(userProductivity),
      backgroundColor: [
        'rgba(255, 99, 132, 0.5)',
        'rgba(54, 162, 235, 0.5)',
        'rgba(255, 206, 86, 0.5)',
        'rgba(75, 192, 192, 0.5)',
        'rgba(153, 102, 255, 0.5)',
      ],
      borderColor: [
        'rgba(255, 99, 132, 1)',
        'rgba(54, 162, 235, 1)',
        'rgba(255, 206, 86, 1)',
        'rgba(75, 192, 192, 1)',
        'rgba(153, 102, 255, 1)',
      ],
      borderWidth: 1
    }]
  };

  // Process data for daily product creation
  const dailyProducts = data.reduce((acc, item) => {
    const date = new Date(item.metadata.timestamp).toLocaleDateString();
    acc[date] = (acc[date] || 0) + (item.metadata.number_of_products || 0);
    return acc;
  }, {});

  const dailyProductsData = {
    labels: Object.keys(dailyProducts),
    datasets: [{
      label: 'Products per Day',
      data: Object.values(dailyProducts),
      backgroundColor: 'rgba(54, 162, 235, 0.5)',
      borderColor: 'rgba(54, 162, 235, 1)',
      borderWidth: 1
    }]
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '20px',
        marginBottom: '20px' 
      }}>
        <div style={{ 
          backgroundColor: 'white',
          padding: '15px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ marginBottom: '15px', color: '#666' }}>Products Created Over Time</h3>
          <Line
            data={timelineData}
            options={{
              responsive: true,
              scales: {
                x: {
                  type: 'time',
                  time: {
                    unit: 'day'
                  },
                  title: {
                    display: true,
                    text: 'Date'
                  }
                },
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Number of Products'
                  }
                }
              }
            }}
          />
        </div>

        <div style={{ 
          backgroundColor: 'white',
          padding: '15px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ marginBottom: '15px', color: '#666' }}>Products by User</h3>
          <Pie
            data={userProductivityData}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom'
                }
              }
            }}
          />
        </div>

        <div style={{ 
          backgroundColor: 'white',
          padding: '15px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ marginBottom: '15px', color: '#666' }}>Daily Product Creation</h3>
          <Bar
            data={dailyProductsData}
            options={{
              responsive: true,
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Number of Products'
                  }
                }
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductAnalytics;